import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { API, imgUrl } from "../lib/api";

const ARROW = "w-8 h-8 rounded-full border border-[#D4AF37]/40 bg-black/50 hover:bg-[#D4AF37] hover:text-black text-[#E5C158] flex items-center justify-center transition-colors";

export const HeroText = ({ b }) => (
  <div className="max-w-2xl space-y-2">
    {b.top_text && <p className="eyebrow !text-[10px] flex items-center gap-1.5" data-testid="hero-top-text"><Sparkles className="w-3 h-3" /> {b.top_text}</p>}
    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.05]" data-testid="hero-heading">
      {b.heading_1} {b.heading_2 && <span className="gold-text italic">{b.heading_2}</span>} {b.heading_3 && <span className="text-gray-300">{b.heading_3}</span>}
    </h1>
    {b.sub_text && <p className="text-gray-400 text-sm max-w-lg" data-testid="hero-sub-text">{b.sub_text}</p>}
    {b.button_text && b.button_link && (b.button_link.startsWith("/")
      ? <Link to={b.button_link} className="btn-gold text-sm !py-2 mt-2" data-testid="hero-cta">{b.button_text} <ArrowRight className="w-4 h-4" /></Link>
      : <a href={b.button_link} target="_blank" rel="noopener noreferrer" className="btn-gold text-sm !py-2 mt-2" data-testid="hero-cta">{b.button_text} <ArrowRight className="w-4 h-4" /></a>)}
  </div>
);

export const FALLBACK = { id: "default", image_url: "https://images.unsplash.com/photo-1769674027488-d28af3f6ccb8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=2000", top_text: "India's premium pre-owned car marketplace", heading_1: "Drive home", heading_2: "excellence.", heading_3: "Across India.", sub_text: "Buy & sell directly with private owners or trusted dealer showrooms in all 36 states & UTs." };

// Homepage hero: admin-managed slides with background image + left-aligned text; children render below (stats, search).
export default function HeroSlider({ slides: given, top, aside, children, className = "" }) {
  const [slides, setSlides] = useState(given || []);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const touch = useRef(null);

  useEffect(() => { if (given) { setSlides(given); setI(0); } }, [given]);
  useEffect(() => {
    if (given) return;
    axios.get(`${API}/hero-banners`).then((r) => setSlides(r.data.length ? r.data : [FALLBACK])).catch(() => setSlides([FALLBACK]));
  }, [given]);
  const list = slides.length ? slides : [FALLBACK];
  const n = list.length;
  useEffect(() => {
    if (n < 2 || paused) return;
    const t = setInterval(() => setI((x) => (x + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n, paused]);
  const cur = list[Math.min(i, n - 1)];

  return (
    <section className={`relative grain overflow-hidden ${className}`} data-testid="hero-slider" data-count={n}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (touch.current != null && Math.abs(dx) > 40) setI((x) => (x + (dx < 0 ? 1 : -1) + n) % n); touch.current = null; }}>
      {list.map((b, k) => <img key={b.id || k} src={imgUrl(b.image_url)} alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${k === i ? "opacity-100" : "opacity-0"}`} />)}
      <div className="absolute inset-0 bg-gradient-to-r from-[#08090C] via-[#08090C]/85 to-[#08090C]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#08090C] via-transparent to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[100px] lg:pt-28 pb-8 w-full">
        {top}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
          <div key={cur.id || i} className="rise"><HeroText b={cur} /></div>
          {aside}
        </div>
        {n > 1 && (
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => setI((x) => (x - 1 + n) % n)} aria-label="Previous slide" className={ARROW} data-testid="hero-prev"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex gap-1.5" data-testid="hero-dots">
              {list.map((b, k) => <button key={b.id || k} onClick={() => setI(k)} aria-label={`Slide ${k + 1}`} className={`h-1.5 rounded-full transition-[width,background-color] ${k === i ? "w-7 bg-[#D4AF37]" : "w-1.5 bg-white/40"}`} data-testid={`hero-dot-${k}`} data-active={k === i} />)}
            </div>
            <button onClick={() => setI((x) => (x + 1) % n)} aria-label="Next slide" className={ARROW} data-testid="hero-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
        {children}
      </div>
    </section>
  );
}
